import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Fab,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import OwnedJourneys from "./components/OwnedJourneys";
import MarketLoadJourneys from "./components/MarketLoadJourneys";
import { commonDateFieldProps, commonTextFieldProps } from "../../utils/styles";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function FixedIncomeTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function FixedIncomeTabPanelProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const FixedIncome: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    vehicleNumber: "",
    startDate: "",
    routeFrom: "",
    routeTo: "",
  });

  const handleFilterChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      vehicleNumber: "",
      startDate: "",
      routeFrom: "",
      routeTo: "",
    });
  };

  const handleNewBooking = () => {
    navigate("/fixed-income/contract-information");
  };

  const handleEditBooking = (id: string) => {
    navigate(`/fixed-income/contract-information/${id}`);
  };

  const handleDailyLogs = (
    id: string,
    vehicleId: string,
    vehicleNumber: string
  ) => {
    navigate(
      `/fixed-income/${id}/logs?vehicleId=${vehicleId}&vehicleNumber=${vehicleNumber}`
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 5,
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.25rem", md: "1.25rem" },
              lineHeight: 1.2,
              mb: 1,
              color: "text.primary",
            }}
          >
            Contract Income
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              fontSize: "0.8rem",
              lineHeight: 1.5,
            }}
          >
            Manage and track all contracts organized by vehicle
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={handleNewBooking}
          sx={{
            display: { xs: "none", md: "flex" },
            px: 3,
            py: 1.5,
            fontSize: "0.7rem",
            fontWeight: 500,
            height: 30,
          }}
        >
          New Contract
        </Button>
      </Box>

      {/* Search & Filter Section */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 1.5 },
          px: { xs: 2, md: 2 },
          mb: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Vehicle Number"
              placeholder="e.g., MH12AB1234"
              value={searchFilters?.vehicleNumber}
              onChange={(e) =>
                handleFilterChange("vehicleNumber", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "0.9rem" }} color="action" />
                  </InputAdornment>
                ),
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              type="date"
              label="Contract Start Date"
              value={searchFilters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              {...commonDateFieldProps}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={7.2}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              sx={{
                fontSize: "0.7rem",
                px: 3,
                alignSelf: { xs: "flex-end", sm: "auto" },
                height: 30,
              }}
            >
              Clear All Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vehicle FixedIncome Groups */}
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="Owned" {...FixedIncomeTabPanelProps(0)} />
            <Tab label="Market Load" {...FixedIncomeTabPanelProps(1)} />
          </Tabs>
        </Box>
        <FixedIncomeTabPanel value={value} index={0}>
          <OwnedJourneys
            handleEditBooking={handleEditBooking}
            handleDailyLogs={handleDailyLogs}
            handleNewBooking={handleNewBooking}
            searchFilters={searchFilters}
          />
        </FixedIncomeTabPanel>
        <FixedIncomeTabPanel value={value} index={1}>
          <MarketLoadJourneys
            handleEditBooking={handleEditBooking}
            handleDailyLogs={handleDailyLogs}
            handleNewBooking={handleNewBooking}
            searchFilters={searchFilters}
          />
        </FixedIncomeTabPanel>
      </Box>

      {/* Floating Action Button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add rental"
          size="large"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            boxShadow: theme.shadows[6],
          }}
          onClick={handleNewBooking}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}
    </Container>
  );
};

export default FixedIncome;
